import { Paper } from "@mui/material";
import { KaminMonitoringRow } from "../../Requests/kamin2leg";
import { printPriceWithDecimalsForTableCell } from "../../helper";
import { toJalaliDateTime } from "../../Utils/date";

const number = (value?: number | null) => value == null || !Number.isFinite(value)
  ? "—" : printPriceWithDecimalsForTableCell(value);

export default function KaminCriterionDiagram({ snapshot }: { snapshot?: KaminMonitoringRow }) {
  const boundaries = [
    { key: "final", title: "مرز نهایی رفع ناترازی", value: snapshot?.finalHedgeMaxCost,
      price: snapshot?.finalHedgePrice, color: "#d97706" },
    { key: "lower", title: "کف عدم حساسیت", value: snapshot?.lowerSensitivityCriterion,
      price: snapshot?.lowerSensitivityPrice, color: "#0284c7" },
    { key: "floor", title: "کف هدف", value: snapshot?.targetLower,
      price: snapshot?.targetLowerPrice, color: "#15803d" },
    { key: "ceiling", title: "سقف هدف", value: snapshot?.targetUpper,
      price: snapshot?.targetUpperPrice, color: "#22c55e" },
    { key: "upper", title: "سقف عدم حساسیت", value: snapshot?.upperSensitivityCriterion,
      price: snapshot?.upperSensitivityPrice, color: "#0369a1" },
  ];
  const valid = boundaries.filter(boundary => boundary.value != null && Number.isFinite(boundary.value));
  const min = valid.length ? Math.min(...valid.map(boundary => boundary.value!)) : 0;
  const max = valid.length ? Math.max(...valid.map(boundary => boundary.value!)) : 0;
  // Desirability increases from right to left, regardless of the numeric direction.
  const higherIsBetter = snapshot?.criterionHigherIsBetter ?? snapshot?.criterionType !== "SignedCost";
  const position = (value: number) => max === min ? 500 : higherIsBetter
    ? 950 - (value - min) * 900 / (max - min) : 50 + (value - min) * 900 / (max - min);
  const lower = snapshot?.targetLower;
  const upper = snapshot?.targetUpper;
  const hasRange = lower != null && upper != null && Number.isFinite(lower) && Number.isFinite(upper);
  const criterion = snapshot?.criterionType === "SignedCost" ? "بهای تمام‌شده" : snapshot?.criterionType;
  return <Paper variant="outlined" className="kamin-criterion-diagram" dir="rtl">
    <div className="kamin-criterion-heading">
      <span>معیار: {criterion || "—"}{snapshot?.criterionUnit === "Percent" ? " (%)" : ""}</span>
      {snapshot && <span>{higherIsBetter ? "مقدار بیشتر مطلوب‌تر است" : "مقدار کمتر مطلوب‌تر است"}</span>}
      <span>قیمت سرخط شکار مقابل: <b dir="ltr">{number(snapshot?.huntTopPrice)}</b></span>
      {snapshot?.timeUtc && <span className="text-slate-500">{toJalaliDateTime(snapshot.timeUtc)}</span>}
    </div>
    {valid.length > 0 ? <svg viewBox="0 0 1000 86" className="kamin-criterion-axis"
      role="img" aria-label="مرزهای معیار و بازه هدف؛ مطلوبیت معیار از راست به چپ افزایش می‌یابد">
      <line x1="50" x2="950" y1="43" y2="43" stroke="#0284c7" strokeWidth="2" />
      {hasRange && <rect x={Math.min(position(lower!), position(upper!))} y="25"
        width={Math.abs(position(upper!) - position(lower!))} height="36"
        fill="#f0fdf4" stroke="#15803d" strokeWidth="2"><title>بازه هدف</title></rect>}
      {valid.map(boundary => <g key={boundary.key}>
        <line x1={position(boundary.value!)} x2={position(boundary.value!)} y1="16" y2="70"
          stroke={boundary.color} strokeWidth="3" />
        <circle cx={position(boundary.value!)} cy="43" r="5" fill={boundary.color}>
          <title>{boundary.title}: معیار {number(boundary.value)}، قیمت {number(boundary.price)}</title>
        </circle>
      </g>)}
    </svg> : <div className="py-3 text-slate-500">رکوردی برای نمایش مرزها وجود ندارد.</div>}
    <div className="kamin-criterion-boundaries">
      {boundaries.map(boundary => <div key={boundary.key} className="kamin-criterion-boundary"
        style={{ borderTopColor: boundary.color }}>
        <div>{boundary.title}</div>
        <div className="kamin-criterion-value"><span>معیار</span><b dir="ltr">{number(boundary.value)}</b></div>
        <div className="kamin-criterion-value"><span>قیمت محاسبه‌شده</span><b dir="ltr">{number(boundary.price)}</b></div>
      </div>)}
    </div>
  </Paper>;
}
